import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Tooltip } from 'devextreme-react';
import moment from 'moment';
import './TrackingProgressBar.scss';
import { ModalNotes } from '../modal-notes/ModalNotes';
import { useApiTracking } from '../../../api/api.tracking';
import { newToast } from '../../index';

interface TrackingProgressBarProps {
  trackingData: any[];
  allStates: any[];
  currentOrder: any;
  onAdvance?: (orderId: number, tmId: number, tkeId: number) => void;
  onReject?: (orderId: number, tmId: number) => void;
  onTrackingUpdate?: (updatedData: any[]) => void;
  highlightedSampleId?: number | null;
}

export const TrackingProgressBar: React.FC<TrackingProgressBarProps> = ({ trackingData, allStates, currentOrder, onAdvance, onReject, onTrackingUpdate, highlightedSampleId }) => {  
  // Estado para controlar qué líneas deben animarse
  const [animatingLines, setAnimatingLines] = useState<Set<string>>(new Set());

  // Estados para el modal de descarte
  const [discardModalVisible, setDiscardModalVisible] = useState<boolean>(false);
  const [selectedTrackingToDiscard, setSelectedTrackingToDiscard] = useState<any>(null);
  const [isDiscarding, setIsDiscarding] = useState<boolean>(false);

  // Estados para el modal de rollback
  const [rollbackModalVisible, setRollbackModalVisible] = useState<boolean>(false);
  const [selectedTrackingToRollback, setSelectedTrackingToRollback] = useState<any>(null);
  const [isRollingBack, setIsRollingBack] = useState<boolean>(false);

  // Estados para el modal de reinicio (muestras descartadas)
  const [reinitModalVisible, setReinitModalVisible] = useState<boolean>(false);
  const [selectedTrackingToReinit, setSelectedTrackingToReinit] = useState<any>(null);
  const [isReiniting, setIsReiniting] = useState<boolean>(false);

  const { discartTracking, rollbackTracking, setTracking } = useApiTracking();

  // Efecto para animar las líneas cuando cambian los datos
  useEffect(() => {
    if (trackingData && trackingData.length > 0) {
      // Animar SOLO la línea que conecta el estado anterior con el estado actual
      trackingData.forEach((tracking) => {
        const currentStateIndex = allStates.findIndex((s) => s.Tke_Id === tracking.Tk_Tke_Id);

        // Solo animar la línea anterior al estado actual (la que se acaba de completar)
        if (currentStateIndex > 0) {
          const lineKey = `${tracking.Tk_Id}-line-${currentStateIndex - 1}`;

          setAnimatingLines((prev) => new Set(prev).add(lineKey));

          // Limpiar la animación después de que termine
          setTimeout(() => {
            setAnimatingLines((prev) => {
              const newSet = new Set(prev);
              newSet.delete(lineKey);
              return newSet;
            });
          }, 1000);
        }
      });
    }
  }, [trackingData, allStates]);

  // Agrupar por tipo de muestra usando useMemo para forzar recálculo
  // ELIMINADO: Ya no agrupamos, mostramos cada tracking individualmente
  const sortedTrackingData = useMemo(() => {
    if (!trackingData || trackingData.length === 0) {
      return [];
    }
    // Ordenar por tipo de muestra y luego por ID para mantener consistencia
    return [...trackingData].sort((a, b) => {
      if (a.Tk_Tm_Id !== b.Tk_Tm_Id) {
        return a.Tk_Tm_Id - b.Tk_Tm_Id;
      }
      return a.Tk_Id - b.Tk_Id;
    });
  }, [trackingData]);

  // TODOS LOS CALLBACKS DEBEN ESTAR ANTES DE LOS RETURNS CONDICIONALES
  const handleAdvanceClick = useCallback(
    (idTk: number, tkeId: number) => {
      if (onAdvance && currentOrder) {
        onAdvance(currentOrder.Ord_Id, idTk, tkeId);
      }
    },
    [onAdvance, currentOrder]
  );

  const handleRejectClick = useCallback(
    (tkId: number) => {
      // Encontrar el tracking correspondiente al tkId
      const tracking = trackingData.find((t) => t.Tk_Id === tkId);
      if (tracking) {
        setSelectedTrackingToDiscard(tracking);
        setDiscardModalVisible(true);
      }
    },
    [trackingData]
  );

  const handleConfirmDiscard = useCallback(
    async (note: string) => {
      if (!selectedTrackingToDiscard || !note) {
        return;
      }

      setIsDiscarding(true);

      try {
        const discardData = {
          IdTk: selectedTrackingToDiscard.Tk_Id,
          IdOrd: selectedTrackingToDiscard.Tk_Ord_Id,
          IdTkEst: selectedTrackingToDiscard.Tk_Tke_Id,
          Notes: note,
        };

        const response = await discartTracking(discardData);

        // Verificar si la respuesta es válida
        if (response && response.length > 0) {
          // Actualizar los datos de tracking
          if (onTrackingUpdate) {
            onTrackingUpdate(response);
          }
          newToast('SUCCESS', 'Muestra descartada correctamente', 3);
          setDiscardModalVisible(false);
          setSelectedTrackingToDiscard(null);
        } else {
          newToast('ERROR', 'Error al descartar la muestra', 3);
        }
      } catch (error) {
        newToast('ERROR', 'Error al descartar la muestra', 3);
      } finally {
        setIsDiscarding(false);
      }
    },
    [selectedTrackingToDiscard, discartTracking, onTrackingUpdate]
  );

  const handleCancelDiscard = useCallback(() => {
    setDiscardModalVisible(false);
    setSelectedTrackingToDiscard(null);
  }, []);

  const handleRollbackClick = useCallback(
    (tkId: number) => {
      // Encontrar el tracking correspondiente al tkId
      const tracking = trackingData.find((t) => t.Tk_Id === tkId);
      if (tracking) {
        setSelectedTrackingToRollback(tracking);
        setRollbackModalVisible(true);
      }
    },
    [trackingData]
  );

  const handleConfirmRollback = useCallback(
    async (note: string) => {
      if (!selectedTrackingToRollback || !note) {
        return;
      }

      setIsRollingBack(true);

      try {
        const response = await rollbackTracking(
          selectedTrackingToRollback.Tk_Ord_Id,
          selectedTrackingToRollback.Tk_Id,
          note
        );

        // Verificar si la respuesta es válida
        if (response && response.length > 0) {
          // Actualizar los datos de tracking
          if (onTrackingUpdate) {
            onTrackingUpdate(response);
          }
          newToast('SUCCESS', 'Muestra retomada correctamente', 3);
          setRollbackModalVisible(false);
          setSelectedTrackingToRollback(null);
        } else {
          newToast('ERROR', 'Error al retomar la muestra', 3);
        }
      } catch (error) {
        newToast('ERROR', 'Error al retomar la muestra', 3);
      } finally {
        setIsRollingBack(false);
      }
    },
    [selectedTrackingToRollback, rollbackTracking, onTrackingUpdate]
  );

  const handleCancelRollback = useCallback(() => {
    setRollbackModalVisible(false);
    setSelectedTrackingToRollback(null);
  }, []);

  const handleReinitClick = useCallback(
    (tkId: number) => {
      // Encontrar el tracking correspondiente al tkId
      const tracking = trackingData.find((t) => t.Tk_Id === tkId);
      if (tracking) {
        setSelectedTrackingToReinit(tracking);
        setReinitModalVisible(true);
      }
    },
    [trackingData]
  );

  const handleConfirmReinit = useCallback(
    async (note: string) => {
      if (!selectedTrackingToReinit || !note || !allStates || allStates.length === 0) {
        return;
      }

      setIsReiniting(true);

      try {
        // Reinicializar significa establecer el primer estado
        const firstState = allStates[0];
        
        // Usar setTracking con el primer estado para reinicializar
        const trackingUpdate = [{
          IdTk: selectedTrackingToReinit.Tk_Id,
          IdOrd: selectedTrackingToReinit.Tk_Ord_Id,
          IdTkEst: 0  , // 0 para reinicializar
        }];

        const response = await setTracking(trackingUpdate);

        // Verificar si la respuesta es válida
        if (response && response.length > 0) {
          // Actualizar los datos de tracking
          if (onTrackingUpdate) {
            onTrackingUpdate(response);
          }
          newToast('SUCCESS', 'Muestra reinicializada correctamente', 3);
          setReinitModalVisible(false);
          setSelectedTrackingToReinit(null);
        } else {
          newToast('ERROR', 'Error al reinicializar la muestra', 3);
        }
      } catch (error) {
        newToast('ERROR', 'Error al reinicializar la muestra', 3);
      } finally {
        setIsReiniting(false);
      }
    },
    [selectedTrackingToReinit, setTracking, onTrackingUpdate, allStates]
  );

  const handleCancelReinit = useCallback(() => {
    setReinitModalVisible(false);
    setSelectedTrackingToReinit(null);
  }, []);

  const calculateTimeDifference = useCallback((startDate: Date, endDate: Date) => {
    const diff = moment(endDate).diff(moment(startDate));
    const duration = moment.duration(diff);

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }, []);

  // AHORA SÍ PUEDEN IR LOS RETURNS CONDICIONALES

  // Si no hay orden seleccionada, mostrar mensaje
  if (!currentOrder) {
    return (
      <div className='tracking-empty-state'>
        <h3 className='empty-title'>Tracking de Muestras</h3>
        <p className='empty-message'>Seleccione una orden para ver el estado de sus muestras</p>
      </div>
    );
  }

  // Si hay orden pero no hay tracking data, mostrar estados vacíos
  if (!trackingData || trackingData.length === 0) {
    return (
      <div className='tracking-progress-container'>
        <div className='tracking-row'>
          <div className='tracking-sample-info'>
            <div className='test-tube test-tube-large'>
              <div className='liquid' style={{ backgroundColor: '#ccc' }}></div>
            </div>
            <div className='sample-text'>
              <span className='sample-title'>Tipo Muestra</span>
              <span className='sample-label'>SIN MUESTRAS</span>
            </div>
          </div>

          <div className='tracking-progress'>
            {allStates.map((state, index) => (
              <div key={state.Tke_Id} className='progress-step'>
                <div className='step-node-container'>
                  <div className='step-node' id={`node-empty-${state.Tke_Id}`}>
                    <div className='step-label'>
                      <div className='state-name'>{state.Tke_Nombre.toUpperCase()}</div>
                    </div>
                  </div>
                  {index < allStates.length - 1 && <div className='step-line'></div>}
                </div>
              </div>
            ))}
          </div>

          <div className='tracking-action'>
            <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>Sin tracking disponible</div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar el componente principal
  return (
    <div className='tracking-progress-container'>
      {sortedTrackingData.map((currentTracking) => {
        const tkId = currentTracking.Tk_Id;
        const isDiscarded = currentTracking.Tk_Tke_Id === null;
        const currentStateIndex = allStates.findIndex((s) => s.Tke_Id === currentTracking.Tk_Tke_Id);
        const totalStates = allStates.length;
        const nextState = currentStateIndex < totalStates - 1 ? allStates[currentStateIndex + 1] : null;
        const isHighlighted = highlightedSampleId === tkId;

        // Obtener información del descarte si existe
        let discardInfo: any = null;
        if (isDiscarded && currentTracking.MonitoreoTracking) {
          // Buscar el último registro en MonitoreoTracking (el descarte)
          const sortedMonitoreo = [...currentTracking.MonitoreoTracking].sort((a, b) => new Date(b.Mtt_Fecha).getTime() - new Date(a.Mtt_Fecha).getTime());
          discardInfo = sortedMonitoreo[0]; // El más reciente es el descarte
        }

        // Obtener información de retoma/rollback si existe
        // Buscar en TODOS los registros de MonitoreoTracking si alguno tiene Mtt_Retomado = true
        let rollbackInfo: any = null;
        if (currentTracking.MonitoreoTracking && Array.isArray(currentTracking.MonitoreoTracking)) {
          // Buscar el registro más reciente que tenga Mtt_Retomado = true
          const rollbackRecords = currentTracking.MonitoreoTracking.filter((m: any) => m.Mtt_Retomado === true).sort((a: any, b: any) => new Date(b.Mtt_Fecha).getTime() - new Date(a.Mtt_Fecha).getTime());

          rollbackInfo = rollbackRecords.length > 0 ? rollbackRecords[0] : null;
        }        return (
          <div key={tkId} className={`tracking-row ${isHighlighted ? 'highlighted' : ''} ${isDiscarded ? 'discarded' : ''} ${rollbackInfo ? 'has-rollback' : ''}`}>
            {/* Tubo y nombre de muestra */}
            <div className='tracking-sample-info'>
              <div className='test-tube test-tube-large'>
                <div className='liquid' style={{ backgroundColor: currentTracking.TipoMuestra.Tm_Color || '#888' }}></div>
              </div>
              <div className='sample-text'>                
                <span className='sample-title'>{currentTracking.TipoMuestra.Tm_Nombre.toUpperCase()}</span>
                <span className='sample-label'>{currentTracking.CodBarras ? currentTracking.CodBarras.Cb_Nombre : '---'}</span>
              </div>
            </div>

            {/* Badge de retomada si existe rollback - SIEMPRE visible si hubo rollback */}
            {rollbackInfo && (
              <>
                <div className='rollback-badge-absolute' id={`rollback-${tkId}`}>
                  <span className='badge-icon'>↩</span>
                  <span className='badge-text'>RETOMADA</span>
                </div>
                <Tooltip target={`#rollback-${tkId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                  <div className='toolTipFilter'>
                    <div>
                      <strong>Muestra Retomada</strong>
                    </div>
                    <div>
                      <strong>Por:</strong> {currentTracking.Usuario?.Us_Nombre} {currentTracking.Usuario?.Us_Apellido1} {currentTracking.Usuario?.Us_Apellido2}
                    </div>
                    <div>
                      <strong>Fecha:</strong> {moment(rollbackInfo.Mtt_Fecha).format('DD/MM/YYYY HH:mm')}
                    </div>
                    {rollbackInfo.Mtt_Notas && (
                      <div>
                        <strong>Motivo:</strong> {rollbackInfo.Mtt_Notas}
                      </div>
                    )}
                  </div>
                </Tooltip>
              </>
            )}

            {/* Contenido condicional: Si está descartada, mostrar info de descarte, si no, mostrar barra de progreso */}
            {isDiscarded ? (
              <div className='tracking-discard-info'>
                <div className='discard-header'>
                  <span className='discard-title'>MUESTRA DESCARTADA</span>
                </div>
                {discardInfo && (
                  <div className='discard-details'>
                    {/* Primera fila: Rechazada por y Fecha en la misma línea */}
                    <div className='discard-detail-row'>
                      <span className='detail-label'>Rechazada por:</span>
                      <span className='detail-value'>
                        {currentTracking.Usuario?.Us_Nombre} {currentTracking.Usuario?.Us_Apellido1} {currentTracking.Usuario?.Us_Apellido2}
                      </span>
                      <span className='detail-label'>Fecha:</span>
                      <span className='detail-value'>{moment(discardInfo.Mtt_Fecha).format('DD/MM/YYYY HH:mm')}</span>
                    </div>
                    {/* Segunda fila: Motivo (solo si existe) */}
                    {discardInfo.Mtt_Notas && (
                      <div className='discard-detail-row notes'>
                        <span className='detail-label'>Motivo:</span>
                        <span className='detail-value'>{discardInfo.Mtt_Notas}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className='tracking-progress'>
                {allStates.map((state, index) => {
                  // Buscar si existe este estado en el tracking actual
                  let monitoreoForThisState: any = null;

                  // Buscar en el MonitoreoTracking del tracking actual
                  const monitoreo = currentTracking.MonitoreoTracking?.find((m: any) => m.Mtt_Tke_Id === state.Tke_Id);
                  if (monitoreo) {
                    monitoreoForThisState = monitoreo;
                  }

                  const isCompleted = monitoreoForThisState !== null;
                  const isCurrent = state.Tke_Id === currentTracking.Tk_Tke_Id;

                  // Calcular tiempo transcurrido para mostrar en la línea DESPUÉS de este nodo
                  let lineTimeLabel = '';
                  if (isCompleted && index < allStates.length - 1) {
                    const nextState = allStates[index + 1];

                    // Buscar el monitoreo del siguiente estado
                    const nextMonitoreo = currentTracking.MonitoreoTracking?.find((m: any) => m.Mtt_Tke_Id === nextState.Tke_Id);

                    if (nextMonitoreo) {
                      // Ambos estados completados: calcular tiempo entre ellos
                      lineTimeLabel = calculateTimeDifference(new Date(monitoreoForThisState.Mtt_Fecha), new Date(nextMonitoreo.Mtt_Fecha));
                    } else if (isCurrent) {
                      // Estado actual sin siguiente completado: tiempo desde que llegó hasta ahora
                      lineTimeLabel = calculateTimeDifference(new Date(monitoreoForThisState.Mtt_Fecha), new Date());
                    }
                  }

                  return (
                    <div key={state.Tke_Id} className='progress-step'>
                      <div className='step-node-container'>
                        <div className={`step-node ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`} id={`node-${tkId}-${state.Tke_Id}`}>
                          {isCompleted && <span className='check'></span>}

                          {/* Leyenda centrada arriba del nodo (solo visible en desktop) */}
                          <div className='step-label'>
                            <div className={`state-name ${isCurrent ? 'current' : ''}`}>{state.Tke_Nombre.toUpperCase()}</div>
                            {/* Mostrar fecha si el estado está completado (tiene monitoreo) */}
                            {monitoreoForThisState && <div className='state-date'>{moment(monitoreoForThisState.Mtt_Fecha).format('DD/MM/YY HH:mm')}</div>}
                          </div>
                        </div>
                        {index < allStates.length - 1 && (
                          <div
                            className={`step-line 
                          ${isCompleted && index < currentStateIndex ? 'completed' : ''} 
                          ${animatingLines.has(`${tkId}-line-${index}`) ? 'animate' : ''}`}>
                            {/* Label de tiempo en el centro de la línea */}
                            {lineTimeLabel && (
                              <div className={`line-time-label ${isCurrent ? 'current-time' : ''}`}>
                                <span className='time-icon'>⏱</span> {lineTimeLabel}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Tooltip del nodo con información completa */}
                      <Tooltip target={`#node-${tkId}-${state.Tke_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                        <div className='toolTipFilter'>
                          <div>
                            <strong>Estado:</strong> {state.Tke_Nombre}
                          </div>
                          {monitoreoForThisState && (
                            <>
                              <div>
                                <strong>Fecha:</strong> {moment(monitoreoForThisState.Mtt_Fecha).format('DD/MM/YYYY HH:mm')}
                              </div>
                              <div>
                                <strong>Usuario:</strong> {currentTracking.Usuario.Us_Nombre} {currentTracking.Usuario.Us_Apellido1} {currentTracking.Usuario.Us_Apellido2}
                              </div>
                            </>
                          )}
                          {!monitoreoForThisState && <div style={{ fontStyle: 'italic', color: '#999' }}>Pendiente</div>}
                        </div>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Botones de acción */}
            <div className='tracking-action'>
              {isDiscarded ? (
                <div id={`btnReinit${tkId}`}>
                  <Button type='danger' stylingMode='outlined' icon='refresh' onClick={() => handleReinitClick(tkId)} text='DESCARTADA' />
                  <Tooltip target={`#btnReinit${tkId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                    <div className='tooltip-reinit'>
                      <strong>Reinicializar muestra</strong>
                      <div>Volver al estado inicial</div>
                    </div>
                  </Tooltip>
                </div>
              ) : currentStateIndex < totalStates - 1 ? (
                <>
                  <div id={`btnReject${tkId}`}>
                    <Button type='danger' stylingMode='contained' icon='close' onClick={() => handleRejectClick(tkId)} />
                    <Tooltip target={`#btnReject${tkId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                      <div className='tooltip-reject'>
                        <strong>Descartar muestra</strong>
                        <div>Marcar como descartada</div>
                      </div>
                    </Tooltip>
                  </div>

                  <div className='action-separator'></div>

                  <div id={`btnAdvance${tkId}`}>
                    <Button type='default' stylingMode='contained' icon='chevronnext' onClick={() => handleAdvanceClick(currentTracking.Tk_Id, currentTracking.Tk_Tke_Id)} />
                    <Tooltip target={`#btnAdvance${tkId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                      <div className='tooltip-advance'>
                        <strong>Avanzar muestra a:</strong>
                        <div>{nextState?.Tke_Nombre}</div>
                      </div>
                    </Tooltip>
                  </div>
                </>
              ) : (
                <>
                  <div id={`btnRollback${tkId}`}>
                    <Button type='normal' stylingMode='contained' icon='undo' onClick={() => handleRollbackClick(tkId)} />
                    <Tooltip target={`#btnRollback${tkId}`} showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                      <div className='tooltip-rollback'>
                        <strong>Retomar muestra</strong>
                        <div>Devolver al estado anterior</div>
                      </div>
                    </Tooltip>
                  </div>

                  <div className='action-separator'></div>

                  <Button type='success' stylingMode='contained' icon='check' disabled />
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Modal de notas para descartar muestra */}
      <ModalNotes visible={discardModalVisible} type='Tracking' currentNote='' onConfirm={handleConfirmDiscard} onCancel={handleCancelDiscard} />

      {/* Modal de notas para retomar muestra */}
      <ModalNotes visible={rollbackModalVisible} type='Rollback' currentNote='' onConfirm={handleConfirmRollback} onCancel={handleCancelRollback} />

      {/* Modal de notas para reinicializar muestra descartada */}
      <ModalNotes visible={reinitModalVisible} type='Rollback' currentNote='' onConfirm={handleConfirmReinit} onCancel={handleCancelReinit} />
    </div>
  );
};
